import { FC, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge } from '@components/atoms/Badge';
import { useTranslation } from '@hooks/useTranslation';
import { IncidentItem } from '@models/dailySiteReport.model';
import { incidentDetailParts, incidentStatusLabel, incidentTypeText } from './labels';

interface IncidentRowProps {
  item: IncidentItem;
  /** Pending-incident rows link to the incident (story-205). */
  linked?: boolean;
}

const formatTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * One incident: type badge + name, status badge, optional description, and the details line
 * (reporter, delay, age). Every story-205 field is optional (stored reports predate them); an old
 * row falls back to the type label alone.
 */
export const IncidentRow: FC<IncidentRowProps> = ({ item, linked = false }) => {
  const t = useTranslation('dailySiteReport');
  // Optional chaining: rendering outside a <Router> (unit tests, previews) must not throw.
  const history = useHistory() as ReturnType<typeof useHistory> | undefined;
  const href = `/incidents/${item.incidentId}`;
  const details = incidentDetailParts(t, item);
  const typeText = incidentTypeText(t, item);
  // Old rows have no name: the type text is the headline and the badge would only repeat it.
  const headline = item.name || typeText;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!history || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    history.push(href);
  };

  return (
    <div data-testid="incident-row">
      <div className="flex flex-wrap items-center gap-2">
        {item.name && (
          <Badge size="sm" variant="secondary">
            {typeText}
          </Badge>
        )}
        {linked ? (
          <a href={href} onClick={handleClick} className="font-medium text-blue-700 hover:underline">
            {headline}
          </a>
        ) : (
          <span className="font-medium">{headline}</span>
        )}
        <Badge size="sm" variant={item.status === 'ACKNOWLEDGED' ? 'primary' : 'warning'}>
          {incidentStatusLabel(t, item.status)}
        </Badge>
      </div>
      {item.description && <p className="mt-1">{item.description}</p>}
      <p className="text-xs text-gray-500 mt-0.5">{[formatTime(item.at), ...details].join(' · ')}</p>
    </div>
  );
};

export default IncidentRow;
